import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DateTime } from 'luxon';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@mui/material';
import {
  ACCOUNTING_SYSTEMS,
  AccountingConnectionType,
  accountingSystemConfig,
  getConnectableAccountingSystems,
} from 'shared';

import { useGetSelectedOrganisation } from '../hooks/useGetSelectedOrganisation';
import { env } from '../../common/lib/env';
import { useOrganisationsIncludeDisconnected } from '../hooks/useOrganisationsIncludeDisconnected';
import { IOption, Select } from '../../common/Atoms/Select';
import { useUser } from '../hooks/useUser';

import { AccountingConnectModal } from './Settings/AccountingConnectModal';
import { CreateInternalOrganisationModal } from './CreateInternalOrganisationModal';

function isAccountingConnectionType(value: string): value is AccountingConnectionType {
  return (Object.values(ACCOUNTING_SYSTEMS) as string[]).includes(value);
}

export function OrgSelection() {
  const { data: user } = useUser();
  const { data: orgs } = useOrganisationsIncludeDisconnected();
  const organisation = useGetSelectedOrganisation();
  const [connectModal, setConnectModal] = useState<{ type: AccountingConnectionType; reconnect: boolean } | null>(null);
  const [internalModalOpen, setInternalModalOpen] = useState(false);

  const navigate = useNavigate();

  const data: IOption[] = useMemo(() => {
    const opts: {
      label: string;
      value: string;
      selected?: boolean;
    }[] = orgs.map(org => {

      return {
        label: `${org.legalName}`,
        value: (org.connected || user?.adminOverride || env.isDev) ? org.id : `reconnect-${org.type}-${org.id}`,
        ...(org.connected ? {} : { description: `Disconnected - click to reconnect` } ),
        selected: organisation?.id === org.id,
      };
    });

    getConnectableAccountingSystems().forEach(type => {
      opts.push({
        label: `+ Connect to ${accountingSystemConfig[type].provider.displayName}`,
        value: `add-${type}`,
      });
    });

    if (env.isDev) {
      opts.push({
        label: `+ Create Internal organisation`,
        value: `create-internal`,
      });
    }

    return opts;
  }, [ orgs, organisation ]);

  const clockHint = useMemo(() => {
    if (!organisation) {
      return null;
    }
    const orgTz = organisation.validatedTimezone;

    const dt = DateTime.local().setZone(orgTz);

    const formatted = dt.toLocaleString(DateTime.DATETIME_MED);

    return (
      <Tooltip title={ `Local Company Time: ${formatted}` }>
        <ClockIcon className={ `h-4 w-4 text-gray-600` } />
      </Tooltip>
    );

  }, [ organisation ]);

  function onChange(e) {
    if (e.value === `create-internal`) {
      setInternalModalOpen(true);

      return;
    }

    // Values are `add-<type>` or `reconnect-<type>-<orgId>`, types contain no dashes
    const [action, type] = String(e.value).split(`-`);
    if (action === `add` || action === `reconnect`) {
      if (isAccountingConnectionType(type)) {
        setConnectModal({ type, reconnect: action === `reconnect` });
      }

      return;
    }

    navigate(`${e.value}`);
  }

  return (
    <>
      <Select
        suffixElement={ clockHint }
        options={ data }
        selected={ organisation?.id }
        onChange={ onChange }
        valueClassname={ `max-w-[120px] lg:max-w-[155px] xl:max-w-[160px] 2xl:max-w-[210px] truncate` }
      />
      <AccountingConnectModal
        type={ connectModal?.type ?? null }
        reconnect={ connectModal?.reconnect }
        onCancel={ () => setConnectModal(null) }
      />
      <CreateInternalOrganisationModal
        open={ internalModalOpen }
        onClose={ () => setInternalModalOpen(false) }
      />
    </>
  );
}
